import { initializeApp, getApps, getApp } from 'firebase/app'
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  onIdTokenChanged,
  User,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
  PhoneAuthProvider,
  signInWithCredential,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth'

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID
}

// Initialize Firebase
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp()
const auth = getAuth(app)

// Set persistence to local storage to maintain auth state across page reloads
if (typeof window !== 'undefined') {
  setPersistence(auth, browserLocalPersistence).catch((error) => {
    console.error('Error setting auth persistence:', error)
  })
}

const googleProvider = new GoogleAuthProvider()
const OAUTH_DEEP_LINK_CHANNEL = 'oauth-deep-link'
const OAUTH_WAIT_TIMEOUT_MS = 3 * 60 * 1000

interface ElectronOAuthBridge {
  openExternal?: (url: string) => Promise<{ success?: boolean } | void>
  onOAuthDeepLink?: (callback: (url: string) => void) => void
  consumePendingOAuthDeepLink?: () => Promise<string | null>
  removeAllListeners?: (channel: string) => void
}

const getElectronOAuthBridge = (): ElectronOAuthBridge | null => {
  if (typeof window === 'undefined') {
    return null
  }
  return (window as any).electronAPI || null
}

const waitForElectronDeepLink = async (bridge: ElectronOAuthBridge): Promise<string> => {
  const pending = await bridge.consumePendingOAuthDeepLink?.()
  if (pending) {
    return pending
  }

  return new Promise((resolve, reject) => {
    let settled = false

    const cleanup = () => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      bridge.removeAllListeners?.(OAUTH_DEEP_LINK_CHANNEL)
    }

    const timeout = setTimeout(() => {
      cleanup()
      reject(new Error('Timed out waiting for Google sign-in callback'))
    }, OAUTH_WAIT_TIMEOUT_MS)

    bridge.onOAuthDeepLink?.((deepLinkUrl: string) => {
      if (!deepLinkUrl || !deepLinkUrl.startsWith('ninx://')) {
        return
      }
      cleanup()
      resolve(deepLinkUrl)
    })
  })
}

const signInWithGoogleViaSystemBrowser = async (
  bridge: ElectronOAuthBridge
): Promise<User> => {
  const response = await fetch('/api/auth/google/system/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ deepLink: 'ninx://auth/callback' }),
  })

  const payload = await response.json().catch(() => ({}))
  if (!response.ok || !payload?.authUrl) {
    throw new Error(payload?.error || 'Failed to start Google system sign-in')
  }

  await bridge.openExternal?.(payload.authUrl)
  const deepLinkUrl = await waitForElectronDeepLink(bridge)
  const parsed = new URL(deepLinkUrl)
  const status = parsed.searchParams.get('status')

  if (status !== 'success') {
    const error = parsed.searchParams.get('error') || 'oauth_failed'
    const message = parsed.searchParams.get('message') || 'Google sign-in was cancelled or failed'
    throw new Error(`${error}: ${message}`)
  }

  const customToken = parsed.searchParams.get('custom_token')
  if (!customToken) {
    throw new Error('Missing custom token in OAuth callback')
  }

  const result = await signInWithCustomToken(auth, customToken)
  return result.user
}

// Helper functions for authentication
export const signInWithGoogle = async () => {
  try {
    const bridge = getElectronOAuthBridge()
    if (bridge?.openExternal && bridge?.onOAuthDeepLink) {
      try {
        return await signInWithGoogleViaSystemBrowser(bridge)
      } catch (systemAuthError) {
        console.warn('[Auth] System browser Google sign-in failed, falling back to popup:', systemAuthError)
      }
    }

    const result = await signInWithPopup(auth, googleProvider)
    // Import is inside function to avoid circular dependency
    // Caller handles cookie-setting once NINX session is confirmed
    return result.user
  } catch (error) {
    console.error('Error signing in with Google', error)
    throw error
  }
}

export const signOutUser = async () => {
  try {
    await firebaseSignOut(auth)
    const { clearNinxSession, removeAuthCookie } = await import('./auth-cookies')
    removeAuthCookie()
    clearNinxSession()
  } catch (error) {
    console.error('Error signing out', error)
    throw error
  }
}

// Phone authentication methods
export const setupRecaptcha = (containerId: string): RecaptchaVerifier => {
  const recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: () => {
      // reCAPTCHA solved, allow signInWithPhoneNumber
    },
    'expired-callback': () => {
      // Response expired, ask user to solve reCAPTCHA again
    }
  })
  
  return recaptchaVerifier
}

export const sendOTP = async (phoneNumber: string, recaptchaVerifier: RecaptchaVerifier): Promise<ConfirmationResult> => {
  try {
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier)
    return confirmationResult
  } catch (error) {
    console.error('Error sending OTP:', error)
    throw error
  }
}

export const verifyOTP = async (confirmationResult: ConfirmationResult, otp: string): Promise<User> => {
  try {
    const result = await confirmationResult.confirm(otp)
    // Caller handles cookie-setting once NINX session is confirmed
    return result.user
  } catch (error) {
    console.error('Error verifying OTP:', error)
    throw error
  }
}

// Firebase Admin for server-side operations
let adminAuth: any = null

const getAdminAuth = () => {
  if (!adminAuth && typeof window === 'undefined') {
    try {
      const admin = require('firebase-admin')
      if (!admin.apps.length) {
        admin.initializeApp({
          credential: admin.credential.cert({
            projectId: process.env.FIREBASE_ADMIN_PROJECT_ID,
            clientEmail: process.env.FIREBASE_ADMIN_CLIENT_EMAIL,
            privateKey: process.env.FIREBASE_ADMIN_PRIVATE_KEY?.replace(/\\n/g, '\n'),
          }),
        })
      }
      adminAuth = admin.auth()
    } catch (error) {
      console.error('Failed to initialize Firebase Admin:', error)
    }
  }
  return adminAuth
}

export const verifyIdToken = async (token: string) => {
  const adminAuth = getAdminAuth()
  if (adminAuth) {
    return await adminAuth.verifyIdToken(token)
  }
  throw new Error('Firebase Admin not available')
}

export { app, auth, onAuthStateChanged, onIdTokenChanged, RecaptchaVerifier }
